/**
 * LoopNest Media Provider Registry
 * Orchestrates all providers with priority and fallback chain.
 * Provider-agnostic: swap providers without touching rendering code.
 */

import { unsplashProvider } from './unsplashProvider';
import { pexelsProvider } from './pexelsProvider';
import { pixabayProvider } from './pixabayProvider';
import { aiGeneratedProvider } from './aiGeneratedProvider';
import { userUploadProvider } from './userUploadProvider';

// Priority order: Pexels (best quality) → Pixabay (CC0) → Unsplash → AI Generated
const PROVIDER_PRIORITY = [
  pexelsProvider,
  pixabayProvider,
  unsplashProvider,
  aiGeneratedProvider,
];

export const mediaProviderRegistry = {
  providers: PROVIDER_PRIORITY,
  userUploadProvider,

  /**
   * Get a provider by ID
   */
  getProvider(id) {
    if (id === 'user-upload') return userUploadProvider;
    return PROVIDER_PRIORITY?.find((p) => p?.id === id) || null;
  },

  /**
   * Get all enabled providers
   */
  getEnabledProviders() {
    return PROVIDER_PRIORITY?.filter((p) => p?.isApproved());
  },

  /**
   * Search with fallback chain
   * Tries providers in priority order, returns first successful result
   */
  async searchWithFallback(query, options = {}) {
    const enabled = this.getEnabledProviders();
    for (const provider of enabled) {
      try {
        const results = await provider?.search(query, options);
        if (results?.length > 0) {
          return { items: results, provider };
        }
      } catch (err) {
        console.warn(`[MediaRegistry] Provider ${provider?.id} failed:`, err);
      }
    }
    // Final fallback: AI generated
    const aiResults = await aiGeneratedProvider?.search(query, options);
    return { items: aiResults, provider: aiGeneratedProvider };
  },

  /**
   * Get image URL for a scene (uses Unsplash Source as primary free option)
   */
  getSceneImageUrl(query, width = 1280, height = 720, seed = 0) {
    return unsplashProvider?.getImageUrl(query, width, height, seed);
  },
};

export default mediaProviderRegistry;
